// Playback of pre-rendered sounds (§13.1): a 32-voice SFX cap that steals the lowest-priority,
// oldest voice; at most 3 bean voices; ±1 semitone playbackRate jitter on non-tonal sounds; pan
// from the screen position and gain falling off with distance from the camera target; the local
// bean's own sounds on the near (+2 dB, drier) input; ghost sounds through the ghost bus.
import type { Mixer } from './mixer';
import { bufferGain, type SoundStore } from './prerender';
import { soundDef, type SoundBus, type SoundKey } from './sounds';
import { clamp } from './util';

export const SFX_VOICES = 32;
export const BEAN_VOICES = 3;
/** Crowd voices: the murmur's two sides and up to five two-sided reactions. */
const CROWD_VOICES = 12;
/** §13.1 ghost pitch: ×0.983 ≈ −30 cents, applied at the source. */
export const GHOST_RATE = 0.983;
/** −6 dB at 15 m from the camera target. */
const FALLOFF_M = 15;
/** Gain per metre below the rim: 1 / (1 + 0.25·depth). */
const DEPTH_FALLOFF = 0.25;

/** Where the camera looks and how world positions map to the screen. */
export class Spatial {
  /** Camera target (smoothed). */
  tx = 0;
  ty = 0;
  /** World metres across the screen when no projector is wired. */
  span = 22;
  private readonly tmp = { x: 0, y: 0, visible: true };

  constructor(public project: ((x: number, y: number, z: number, out: { x: number; y: number; visible: boolean }) => void) | null = null, public screenWidth: () => number = () => 1) {}

  /** §13.1: clamp((screenX − 0.5) × 1.6, −1, 1). */
  pan(x: number, y: number, z = 0): number {
    let sx: number;
    if (this.project) {
      this.project(x, y, z, this.tmp);
      const w = Math.max(1, this.screenWidth());
      sx = this.tmp.x / w;
    } else {
      // The gameplay camera looks down −y with yaw 0 (§12.7): screen x follows world x.
      sx = 0.5 + (x - this.tx) / this.span;
    }
    return Number.isFinite(sx) ? clamp((sx - 0.5) * 1.6, -1, 1) : 0;
  }

  /**
   * Distance falloff from the camera target (−6 dB at 15 m); below the arena rim (z < 0: a bean
   * falling into the void) the sound also recedes, ≈ −5 dB at the kill depth.
   */
  gain(x: number, y: number, z = 0): number {
    const d = Math.hypot(x - this.tx, y - this.ty);
    const depth = z < 0 ? 1 / (1 + -z * DEPTH_FALLOFF) : 1;
    return (FALLOFF_M / (FALLOFF_M + d)) * depth;
  }
}

export interface PlayOpts {
  /** Context time to start (default now). */
  when?: number;
  /** Linear gain multiplier. */
  gain?: number;
  /** Playback-rate multiplier (pitch). */
  rate?: number;
  /** World position (pan + distance) or an explicit pan. */
  x?: number;
  y?: number;
  z?: number;
  pan?: number;
  /** The local bean's own sound. */
  near?: boolean;
  ghost?: boolean;
  /** Per-voice low-pass cutoff, Hz. */
  lowpass?: number;
  loop?: boolean;
  pri?: number;
  /** Skip the ±1 semitone jitter. */
  exact?: boolean;
  variant?: number;
  /** Push the sound's caption (default true; the engine reads it, the player ignores it). */
  caption?: boolean;
  /** Start this far into the buffer, seconds (a second copy of a loop, out of phase). */
  offset?: number;
}

export interface Voice {
  readonly key: SoundKey;
  readonly pri: number;
  readonly start: number;
  readonly src: AudioBufferSourceNode;
  readonly gain: GainNode;
  readonly panner: StereoPannerNode;
  readonly filter: BiquadFilterNode | null;
  /** The playback rate it started at (jitter, ghost and replay scale included). */
  readonly baseRate: number;
  ended: boolean;
  stop(fade?: number): void;
  setPos(x: number, y: number, z?: number): void;
}

type Pool = 'sfx' | 'voice' | 'crowd' | 'ui';

function poolOf(bus: SoundBus): Pool {
  return bus;
}

export class SfxPlayer {
  private readonly pools: Record<Pool, Voice[]> = { sfx: [], voice: [], crowd: [], ui: [] };
  private readonly lastVariant = new Map<SoundKey, number>();
  private rand = Math.random;
  /** Global rate multiplier (replay slow-motion). */
  rateScale = 1;
  /** Gain multiplier for non-local SFX and bean voices (the quieter menu diorama). */
  ambientScale = 1;

  constructor(private readonly ctx: AudioContext, private readonly mixer: Mixer, private readonly store: SoundStore, readonly spatial: Spatial) {}

  setRandom(r: () => number): void {
    this.rand = r;
  }

  ready(key: SoundKey): boolean {
    return this.store.has(key);
  }

  /** Active voices in a pool (diagnostics). */
  count(pool: Pool): number {
    return this.pools[pool].length;
  }

  play(key: SoundKey, o: PlayOpts = {}): Voice | null {
    const bufs = this.store.get(key);
    if (!bufs || bufs.length === 0) return null;
    const def = soundDef(key);
    const pool = poolOf(def.bus);
    const pri = o.pri ?? def.pri;
    const cap = pool === 'sfx' ? SFX_VOICES : pool === 'voice' ? BEAN_VOICES : pool === 'crowd' ? CROWD_VOICES : 16;
    const list = this.pools[pool];
    if (list.length >= cap && !this.steal(list, pri)) return null;

    const ctx = this.ctx;
    const now = ctx.currentTime;
    const when = Math.max(now, o.when ?? now);
    let vi = o.variant ?? Math.floor(this.rand() * bufs.length);
    if (o.variant === undefined && bufs.length > 1 && vi === this.lastVariant.get(key)) vi = (vi + 1) % bufs.length;
    this.lastVariant.set(key, vi);
    const src = ctx.createBufferSource();
    src.buffer = bufs[Math.min(vi, bufs.length - 1)];
    let rate = (o.rate ?? 1) * this.rateScale;
    if (!def.tonal && !o.exact) rate *= 2 ** ((this.rand() * 2 - 1) / 12);
    if (o.ghost) rate *= GHOST_RATE;
    src.playbackRate.value = rate;
    if (o.loop || def.loop) src.loop = true;

    const gain = ctx.createGain();
    const buffer = src.buffer as AudioBuffer;
    let g = (o.gain ?? 1) * bufferGain(buffer);
    const positional = o.x !== undefined && o.y !== undefined;
    if (positional) g *= this.spatial.gain(o.x as number, o.y as number, o.z ?? 0);
    if (!o.near && (pool === 'sfx' || pool === 'voice')) g *= this.ambientScale;
    gain.gain.value = g;
    const panner = ctx.createStereoPanner();
    panner.pan.value = o.pan ?? (positional ? this.spatial.pan(o.x as number, o.y as number, o.z ?? 0) : 0);
    let filter: BiquadFilterNode | null = null;
    src.connect(gain);
    if (o.lowpass !== undefined) {
      filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = o.lowpass;
      filter.Q.value = 0.7;
      gain.connect(filter).connect(panner);
    } else {
      gain.connect(panner);
    }
    panner.connect(this.destination(def.bus, o));
    src.start(when, o.offset !== undefined ? Math.max(0, o.offset) % buffer.duration : 0);

    const spatial = this.spatial;
    const ambient = (): number => (pool === 'sfx' || pool === 'voice' ? this.ambientScale : 1);
    const voice: Voice = {
      key, pri, start: when, src, gain, panner, filter, baseRate: rate, ended: false,
      stop(fade = 0.04) {
        if (voice.ended) return;
        const t = ctx.currentTime;
        gain.gain.cancelScheduledValues(t);
        gain.gain.setTargetAtTime(0, t, Math.max(0.003, fade / 4));
        try {
          src.stop(Math.max(t, when) + fade);
        } catch {
          // Already stopped.
        }
      },
      setPos(x, y, z = 0) {
        const t = ctx.currentTime;
        panner.pan.setTargetAtTime(spatial.pan(x, y, z), t, 0.03);
        gain.gain.setTargetAtTime((o.gain ?? 1) * bufferGain(buffer) * spatial.gain(x, y, z) * (o.near ? 1 : ambient()), t, 0.05);
      },
    };
    src.onended = () => {
      voice.ended = true;
      const i = list.indexOf(voice);
      if (i >= 0) list.splice(i, 1);
      src.disconnect();
      gain.disconnect();
      filter?.disconnect();
      panner.disconnect();
    };
    list.push(voice);
    return voice;
  }

  /** Frees a slot from the lowest-priority, oldest voice no more important than `pri`. */
  private steal(list: Voice[], pri: number): boolean {
    let victim: Voice | null = null;
    for (const v of list) {
      if (v.pri > pri || v.src.loop) continue;
      if (!victim || v.pri < victim.pri || (v.pri === victim.pri && v.start < victim.start)) victim = v;
    }
    if (!victim) return false;
    victim.stop(0.015);
    const i = list.indexOf(victim);
    if (i >= 0) list.splice(i, 1);
    victim.ended = true;
    return true;
  }

  private destination(bus: SoundBus, o: PlayOpts): AudioNode {
    if (o.ghost) return this.mixer.ghost;
    switch (bus) {
      case 'ui': return this.mixer.ui;
      case 'voice': return this.mixer.voice;
      case 'crowd': return this.mixer.crowd;
      default: return o.near ? this.mixer.sfxNear : this.mixer.sfxFar;
    }
  }

  stopAll(fade = 0.05): void {
    for (const list of Object.values(this.pools)) for (const v of [...list]) v.stop(fade);
  }
}
